import {
  isCartBundleNotFoundError,
  isCartDependencyError,
  isCartOperationInProgressError,
  isCartUpsertConcurrencyError
} from '@/lib/cartErrors'
import { measureCartUpsert } from '@/lib/cartUpsertTelemetry'
import Sunny16BaseService from '@/services/Sunny16BaseService'

import { CadyMarket } from '@/types/CadyMarket'

import {
  ApplyPromoCodeStorefrontDto,
  CartBundleUpdateStorefrontDto,
  CartBundleUpsertStorefrontDto,
  CartItemCustomizationDto,
  CartItemImageCustomizationDto,
  CartStorefrontDto,
  CartStorefrontValidationResultDto,
  PromoCodeResultStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

// A busy cart's 409 is handled by the caller's feedback, so it stays out of New Relic.
const CART_BUSY_EXPECTED = { _expectedError: isCartOperationInProgressError } as object
// Cart responses are small (a cart, not a catalog), and normal server-side p99 is under 3s. This
// bounds how long the client will sit in an unknown state before refetching, not how long the
// server is allowed to take: during the 2026-09-16 incident cart writes ran to p95 120s+, far past
// any point a customer was still waiting.
const CART_REQUEST_TIMEOUT_MS = 20_000

class Sunny16CommerceCartService extends Sunny16BaseService {
  constructor() {
    super(import.meta.env.VITE_SUNNY_16_CART_API_BASE_URL, CART_REQUEST_TIMEOUT_MS)
  }

  async getCart(cartId: string, securityKey: string): Promise<CartStorefrontDto> {
    try {
      const response = await this.axiosInstance.get(`/${cartId}/${securityKey}`)
      return response.data
    } catch (error) {
      console.error('Error fetching cart:', error)
      throw error
    }
  }

  async upsertCartBundle(
    inputCartBundle: CartBundleUpsertStorefrontDto,
    cadyProductLine: CadyMarket | null,
    cartId?: string,
    securityKey?: string,
    subjectId?: number | null,
    schoolId?: number | null,
    eventDate?: string | null,
    catalogUpsellId?: string | null,
    sourceHint?: string | null
  ): Promise<CartStorefrontDto> {
    try {
      const queryParams = new URLSearchParams()
      if (cartId) queryParams.append('cartId', cartId)
      if (securityKey) queryParams.append('securityKey', securityKey)
      if (subjectId) queryParams.append('externalSubjectIdentifier', subjectId.toString())
      if (schoolId) queryParams.append('externalEventIdentifier', schoolId.toString())
      if (cadyProductLine)
        queryParams.append('externalAdditionalIdentifier', cadyProductLine.toString())
      if (eventDate) queryParams.append('externalEventDate', eventDate.toString())
      if (catalogUpsellId) queryParams.append('catalogUpsellId', catalogUpsellId)
      if (sourceHint) queryParams.append('sourceHint', sourceHint)

      let url

      if (inputCartBundle.cartBundleId) {
        url = `/${inputCartBundle.cartBundleId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      } else {
        url = `/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      }

      // Send the request, timed — the reward-unlock sequence trails this round
      // trip, so its duration is what the debug harness's latency presets are
      // set from. See `lib/cartUpsertTelemetry.ts`.
      const response = await measureCartUpsert(
        { cadyMarket: cadyProductLine, isNewCartBundle: !inputCartBundle.cartBundleId },
        () =>
          this.axiosInstance.post<CartStorefrontDto>(url, inputCartBundle, {
            _expectedError: (e: unknown) =>
              isCartUpsertConcurrencyError(e) ||
              isCartDependencyError(e) ||
              isCartBundleNotFoundError(e) ||
              isCartOperationInProgressError(e)
          } as object)
      )
      return response.data
    } catch (error) {
      if (
        !isCartUpsertConcurrencyError(error) &&
        !isCartDependencyError(error) &&
        !isCartBundleNotFoundError(error) &&
        !isCartOperationInProgressError(error)
      ) {
        console.error('Error upserting cart bundle: ', error)
      }
      throw error
    }
  }

  async updateCartBundleQuantity(
    cartId: string,
    securityKey: string,
    cartBundleId: string,
    quantity: number
  ): Promise<CartStorefrontDto> {
    try {
      const response = await this.axiosInstance.put(
        `/${cartId}/${securityKey}/${cartBundleId}/${quantity}`,
        undefined,
        CART_BUSY_EXPECTED
      )
      return response.data
    } catch (error) {
      console.error('Error updating cart bundle quantity: ', error)
      throw error
    }
  }

  async updateCartConfiguration(
    cartId: string,
    securityKey: string,
    cartConfiguration: CartBundleUpdateStorefrontDto[]
  ) {
    try {
      const response = await this.axiosInstance.post(
        `/config/${cartId}/${securityKey}`,
        cartConfiguration,
        CART_BUSY_EXPECTED
      )
      return response.data
    } catch (error) {
      console.error('Error updating cart configuration: ', error)
      throw error
    }
  }

  async upsertCartCustomizations(
    cartId: string,
    securityKey: string,
    cartItemCustomizations: (CartItemCustomizationDto | CartItemImageCustomizationDto)[]
  ): Promise<CartStorefrontDto> {
    try {
      const response = await this.axiosInstance.post(
        `/customizations/${cartId}/${securityKey}`,
        cartItemCustomizations,
        CART_BUSY_EXPECTED
      )
      return response.data
    } catch (error) {
      console.error('Error upserting cart customizations: ', error)
      throw error
    }
  }

  async deleteCartBundle(
    cartId: string,
    securityKey: string,
    cartBundleId: string
  ): Promise<CartStorefrontDto> {
    try {
      const response = await this.axiosInstance.delete(
        `/${cartId}/${securityKey}/${cartBundleId}`,
        {
          _expectedError: (e: unknown) =>
            isCartBundleNotFoundError(e) || isCartOperationInProgressError(e)
        } as object
      )
      return response.data
    } catch (error) {
      if (!isCartBundleNotFoundError(error) && !isCartOperationInProgressError(error)) {
        console.error('Error deleting cart bundle: ', error)
      }
      throw error
    }
  }

  async deleteAllCartBundles(cartId: string, securityKey: string): Promise<CartStorefrontDto> {
    try {
      const response = await this.axiosInstance.delete(
        `/${cartId}/${securityKey}`,
        CART_BUSY_EXPECTED
      )
      return response.data
    } catch (error) {
      console.error('Error deleting all cart bundles: ', error)
      throw error
    }
  }

  async applyPromoCode(
    cartId: string,
    securityKey: string,
    data: ApplyPromoCodeStorefrontDto
  ): Promise<PromoCodeResultStorefrontDto> {
    try {
      const response = await this.axiosInstance.post(
        `/promotions/${cartId}/${securityKey}`,
        data,
        CART_BUSY_EXPECTED
      )
      return response.data
    } catch (error) {
      console.error('Error applying promo code: ', error)
      throw error
    }
  }

  // `applyInCartUpsells` tells Sunny16 whether this prepare may auto-add the
  // in-cart upsell bundles (`Source=AutomaticUpsell`). The storefront sends
  // `true` on the first prepare of a cart and `false` on every later one, so
  // an upsell the customer removed is not re-added on the next checkout
  // landing. Until Sunny16 honours the parameter it is ignored and `/prepare`
  // behaves as before. See `docs/features/2026.09.14-in-cart-upsell-auto-add-once/`.
  async prepareCart(
    cartId: string,
    securityKey: string,
    applyInCartUpsells: boolean
  ): Promise<CartStorefrontDto> {
    try {
      const queryParams = new URLSearchParams({
        applyInCartUpsells: applyInCartUpsells.toString()
      })
      const response = await this.axiosInstance.post(
        `/prepare/${cartId}/${securityKey}?${queryParams.toString()}`,
        undefined,
        CART_BUSY_EXPECTED
      )
      return response.data
    } catch (error) {
      console.error('Error preparing cart: ', error)
      throw error
    }
  }

  async validateCart(
    cartId: string,
    securityKey: string
  ): Promise<CartStorefrontValidationResultDto> {
    try {
      const response = await this.axiosInstance.get(`/validate/${cartId}/${securityKey}`)
      return response.data
    } catch (error) {
      console.error('Error validating cart:', error)
      throw error
    }
  }
}

export default Sunny16CommerceCartService
